import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Megaphone, ArrowRight } from "lucide-react";
import { ANNOUNCEMENT_BANNER } from "../data/home-data";

export default function AnnouncementBanner({ onVisibilityChange, onApplyNow, onHeightChange }) {
    // Always show on every page load/refresh (no persistence). Closing it
    // only hides it for the current view; it returns on the next refresh.
    const [visible, setVisible] = useState(true);
    const contentRef = useRef(null);

    useEffect(() => {
        if (onVisibilityChange) onVisibilityChange(visible);
    }, [visible, onVisibilityChange]);

    // Report the banner's real height so the navbar can sit just below it —
    // this keeps working even when the text wraps to two lines on mobile.
    useEffect(() => {
        if (!visible) {
            onHeightChange?.(0);
            return;
        }
        const el = contentRef.current;
        if (!el) return;
        const report = () => onHeightChange?.(el.offsetHeight);
        report();
        const ro = new ResizeObserver(report);
        ro.observe(el);
        return () => ro.disconnect();
    }, [visible, onHeightChange]);

    const dismiss = () => setVisible(false);
    const handleApplyNow = () => onApplyNow?.();

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-aiblue via-blue-600 to-aiorange text-white overflow-hidden"
                >
                    {/* px-10 reserves room on the right so the close (✕) never
                        overlaps the content/Apply Now; flex-wrap lets it stack
                        on narrow screens instead of overflowing. */}
                    <div
                        ref={contentRef}
                        className="relative max-w-7xl mx-auto px-10 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5"
                    >
                        <div className="flex items-center gap-2.5">
                            <Megaphone className="hidden sm:block h-4 w-4 shrink-0 text-white/80" />
                            <p className="text-xs sm:text-sm font-medium text-center">
                                {ANNOUNCEMENT_BANNER.emoji}{" "}
                                <span className="font-bold">{ANNOUNCEMENT_BANNER.boldText}</span>{" "}
                                {ANNOUNCEMENT_BANNER.text}
                            </p>
                        </div>

                        <button
                            onClick={handleApplyNow}
                            className="inline-flex items-center gap-1.5 px-3 py-1 sm:px-4 sm:py-1.5 bg-white text-aiblue rounded-full text-xs sm:text-sm font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all animate-pulse"
                        >
                            Apply Now
                            <ArrowRight className="h-3.5 w-3.5" />
                        </button>

                        <button
                            onClick={dismiss}
                            aria-label="Dismiss announcement"
                            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/20 transition"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
